import type { AdminBillingReplayResult } from '@steado/admin-contract/billing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { Field, Fields, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDateTime } from '@/lib/format';

import { billingEventQueryOptions, replayBillingEvent } from './billing-query';

interface BillingEventDrawerProps {
  readonly eventId: string;
  readonly onClose: () => void;
}

export function BillingEventDrawer({ eventId, onClose }: BillingEventDrawerProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(billingEventQueryOptions(api, eventId));

  const [isConfirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AdminBillingReplayResult | null>(null);

  const replay = useMutation({
    mutationFn: () => replayBillingEvent(api, eventId),
    onSuccess: (replayed) => {
      setResult(replayed);
      setConfirming(false);
      // The subscription list behind the drawer may have just changed.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
    // Never retried: each attempt re-applies an entitlement change and writes
    // an audit row.
    retry: false,
  });

  const event = query.data;
  const blocked = event !== undefined && !event.replayable;

  return (
    <>
      <Drawer
        title={event === undefined ? 'Delivery' : event.type}
        {...(event === undefined ? {} : { subtitle: event.revenueCatEventId })}
        onClose={onClose}
        footer={
          event === undefined ? undefined : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setConfirming(true);
              }}
              disabled={!can.mutate || blocked || replay.isPending}
              title={
                blocked
                  ? (event.replayBlockedReason ?? undefined)
                  : can.mutate
                    ? undefined
                    : 'Replaying a delivery requires the admin role.'
              }
            >
              {replay.isPending ? 'Replaying…' : 'Replay…'}
            </Button>
          )
        }
      >
        {query.isPending ? <TabSkeleton rows={4} /> : null}
        {query.isError ? (
          <TabError message={query.error.message} onRetry={() => void query.refetch()} />
        ) : null}

        {event === undefined ? null : (
          <div className="flex flex-col gap-5">
            <Panel title="Delivery">
              <Fields>
                <Field label="Type" value={event.type} />
                <Field label="RevenueCat id" value={event.revenueCatEventId} mono />
                <Field label="Received" value={formatDateTime(event.createdAt)} />
                <Field
                  label="Subscriber"
                  // Null is not "unknown user" — it is a delivery for an
                  // app_user_id that was never one of ours.
                  value={
                    event.userId === null ? (
                      <span className="text-muted-foreground">not a Steado user</span>
                    ) : (
                      (event.displayName ?? event.userId)
                    )
                  }
                />
              </Fields>
            </Panel>

            {result === null ? null : (
              <Panel
                title="Replay result"
                action={
                  <BadgeCell
                    label={result.applied ? 'applied' : 'no change'}
                    tone={result.applied ? 'ok' : 'mute'}
                    dot
                  />
                }
              >
                {result.applied ? (
                  <Fields>
                    <Field label="Status" value={result.subscription?.status ?? null} />
                    <Field label="Entitlement" value={result.subscription?.entitlement ?? null} />
                    <Field label="Renews" value={result.subscription?.willRenew === true ? 'yes' : 'no'} />
                    <Field
                      label="Period ends"
                      value={
                        result.subscription?.currentPeriodEnd == null
                          ? null
                          : formatDateTime(result.subscription.currentPeriodEnd)
                      }
                    />
                  </Fields>
                ) : (
                  <p className="text-sm leading-relaxed text-muted-foreground">{result.skippedReason}</p>
                )}
              </Panel>
            )}

            <Panel title="Payload">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
              {blocked ? (
                <p role="note" className="mt-3 text-[13px] leading-relaxed text-warn-fg">
                  {event.replayBlockedReason}
                </p>
              ) : null}
            </Panel>

            {replay.isError ? (
              <Alert role="alert" tone="err">
                <AlertDescription>{replay.error.message}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Drawer>

      {isConfirming && event !== undefined ? (
        <ConfirmDialog
          title={`Replay ${event.type}?`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                The stored payload is re-applied through the same rules the live webhook uses. This can change
                what the user is entitled to.
              </p>
              <p>
                RevenueCat’s own duplicate protection is bypassed on purpose. That guard exists to stop its
                retries double-applying, and this is a deliberate re-application.
              </p>
            </div>
          }
          confirmLabel="Replay"
          isPending={replay.isPending}
          {...(replay.isError ? { error: replay.error.message } : {})}
          onConfirm={() => {
            replay.mutate();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : null}
    </>
  );
}
